import React from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Marker } from 'react-leaflet';
import { Box, Typography, Paper, Grid } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const ComplaintGeoMap = ({ geoHotspots = [], center = null, zoom = null }) => {
  // Default center (center of Pakistan) or use provided center
  const defaultCenter = center || [30.3753, 69.3451]; // Pakistan center
  const defaultZoom = zoom ?? 5;

  // Pakistan Districts Coordinates Mapping
  const districtCoordinates = {
    'Karachi': [24.8607, 67.0011],
    'Lahore': [31.5204, 74.3587],
    'Faisalabad': [31.4185, 72.9800],
    'Rawalpindi': [33.5731, 73.1898],
    'Multan': [30.1575, 71.4454],
    'Hyderabad': [25.3548, 68.3639],
    'Peshawar': [34.0151, 71.5789],
    'Quetta': [30.1798, 67.0073],
    'Islamabad': [33.7294, 73.1882],
    'Gujranwala': [32.1814, 74.1854],
    'Sialkot': [32.4925, 74.5294],
    'Sargodha': [32.0856, 72.6422],
    'Jhang': [31.2956, 72.3180],
    'Sahiwal': [30.6681, 73.1114],
    'Okara': [30.8061, 73.4503],
    'Bahawalpur': [29.3956, 71.6722],
    'Dera Ghazi Khan': [28.7641, 70.6417],
    'Dera Ismail Khan': [31.8254, 70.9030],
    'Kasur': [31.1191, 74.4380],
    'Narowal': [32.1165, 74.9220],
    'Chakwal': [32.9320, 72.8544],
    'Attock': [33.7683, 72.7283],
    'Jhelum': [32.9344, 73.7322],
    'Gujrat': [32.1743, 74.0779],
    'Mandi Bahauddin': [32.5854, 74.6294],
    'Mirpur Khas': [25.5280, 69.0127],
    'Sukkur': [27.7046, 68.8734],
    'Jacobabad': [28.2717, 68.4385],
    'Shikarpur': [27.9607, 68.6341],
    'Larkana': [27.5614, 68.2106],
    'Dadu': [26.7833, 67.9667],
    'Khairpur': [27.5211, 68.7597],
    'Thatta': [24.7511, 67.8900],
    'Badin': [24.6455, 68.4082],
    'Tando Muhammad Khan': [25.0141, 68.5439],
    'Tando Allahyar': [25.2659, 68.6283],
    'Sanghar': [25.9416, 68.9551],
    'Nawabshah': [26.2150, 68.4029],
    'Mardan': [34.1971, 72.0386],
    'Swabi': [34.1338, 72.4646],
    'Mingora': [34.7681, 72.4456],
    'Mansehra': [34.3230, 73.1865],
    'Abbottabad': [34.1495, 73.2131],
    'Haripur': [33.9981, 72.9292],
    'Kohat': [33.5841, 71.4314],
    'Karak': [33.1126, 71.1084],
    'Hangu': [33.5300, 71.2100],
    'Bannu': [32.7827, 70.6285],
    'Lakki Marwat': [32.5267, 70.9201],
    'Tank': [32.7589, 70.3840],
    'Zhob': [31.3180, 69.4589],
    'Loralai': [30.3589, 68.5762],
    'Barkhan': [30.4785, 68.4780],
    'Musakhel': [30.6475, 68.0820],
    'Jhal Magsi': [28.4267, 66.8262],
    'Sibi': [29.5547, 67.8633],
    'Kohlu': [28.9889, 67.4803],
    'Kachhi': [28.1589, 66.5370],
    'Gwader': [25.1271, 62.3289],
    'Turbat': [26.8546, 65.8971],
    'Panjgur': [26.9394, 65.1404],
    'Awaran': [26.1105, 65.3987],
    'Lasbela': [26.5420, 66.6231],
    'Khuzdar': [27.8138, 66.6292],
    'Mastung': [29.8250, 67.3983],
    'Kalat': [29.0275, 67.4684],
    'Washuk': [28.7542, 65.3467],
    'Kharan': [28.5742, 65.4078],
    'Gilgit': [35.9216, 74.3114],
    'Skardu': [35.2895, 75.6245],
    'Hunza': [36.8466, 74.8772],
    'Nagar': [36.4160, 74.8815],
    'Ghizer': [36.7667, 73.5167],
    'Gujranwala City': [32.1814, 74.1854],
    'Muzaffarabad': [34.3708, 73.4811],
    'Mirpur': [33.1422, 73.7274],
    'Bhimber': [32.5058, 73.5066],
    'Kotli': [32.7897, 73.9041]
  };

  // Get coordinates for district
  const getDistrictCoordinates = (districtName) => {
    if (!districtName) return null;

    // Try exact match first
    if (districtCoordinates[districtName]) {
      return districtCoordinates[districtName];
    }

    // Try case-insensitive match
    const key = Object.keys(districtCoordinates).find(
      k => k.toLowerCase() === districtName.toLowerCase()
    );

    if (key) {
      return districtCoordinates[key];
    }

    // Try partial match (substring)
    const partialKey = Object.keys(districtCoordinates).find(
      k => k.toLowerCase().includes(districtName.toLowerCase()) ||
        districtName.toLowerCase().includes(k.toLowerCase())
    );

    if (partialKey) {
      return districtCoordinates[partialKey];
    }

    return null;
  };

  // Generate map data using actual district coordinates
  const generateMapData = () => {
    if (!geoHotspots || geoHotspots.length === 0) {
      return [];
    }

    return geoHotspots.map((spot, idx) => {
      // Try to get coordinates from API data first
      let lat = spot.latitude || spot.lat;
      let lng = spot.longitude || spot.lng;

      // If no coordinates in API, use district mapping
      if (!lat || !lng) {
        const coords = getDistrictCoordinates(spot.district_name);
        if (coords) {
          [lat, lng] = coords;
        } else {
          // Fallback to default center
          [lat, lng] = defaultCenter;
        }
      }

      // Ensure coordinates are valid numbers
      lat = parseFloat(lat);
      lng = parseFloat(lng);

      // Validate coordinates are within reasonable bounds
      if (isNaN(lat) || isNaN(lng) || lat < 24 || lat > 37 || lng < 60 || lng > 78) {
        const coords = getDistrictCoordinates(spot.district_name);
        if (coords) {
          [lat, lng] = coords;
        } else {
          [lat, lng] = defaultCenter;
        }
      }

      const complaintCount = parseInt(spot.complaint_density || 0);
      console.log(`📍 District: ${spot.district_name} | Lat: ${lat.toFixed(4)}, Lng: ${lng.toFixed(4)} | Complaints: ${complaintCount} | Resolved: ${spot.resolved_here}`);

      return {
        name: spot.district_name || `District ${idx + 1}`,
        complaints: complaintCount,
        resolved: parseInt(spot.resolved_here || 0),
        density: complaintCount,
        resolution_rate: parseFloat(spot.local_resolution_rate || 0),
        lat: lat,
        lng: lng
      };
    });
  };

  const mapData = generateMapData();

  // Find max density for normalization
  const maxDensity = mapData.length > 0 ? Math.max(...mapData.map(d => d.density)) : 100;

  // Calculate circle radius based on complaint count (normalized)
  const getRadius = (density) => {
    const normalized = (density / maxDensity) * 100;
    return Math.max(8, Math.min(45, (normalized / 100) * 40));
  };

  // Get color based on normalized density percentage
  const getColor = (density) => {
    const normalized = (density / maxDensity) * 100;
    if (normalized > 70) return '#ef4444'; // Red - Very High
    if (normalized > 50) return '#f59e0b'; // Orange - High
    if (normalized > 30) return '#eab308'; // Yellow - Medium
    return '#10b981'; // Green - Low
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {mapData.length > 0 ? (
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          style={{ width: '100%', height: '100%', borderRadius: '8px', border: '2px solid #e5e7eb' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {mapData.map((location, idx) => {
            const labelIcon = L.divIcon({
              html: `<div style="
                background: white;
                color: #1f2937;
                font-weight: 700;
                font-size: 12px;
                padding: 4px 6px;
                border-radius: 4px;
                border: 2px solid ${getColor(location.density)};
                box-shadow: 0 2px 4px rgba(0,0,0,0.15);
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 30px;
                text-align: center;
                cursor: pointer;
                transition: all 0.2s ease;
              ">${location.complaints}</div>`,
              className: 'complaint-label',
              iconSize: [40, 20],
              iconAnchor: [20, 10],
              popupAnchor: [0, -10]
            });

            return (
              <React.Fragment key={idx}>
                <CircleMarker
                  center={[location.lat, location.lng]}
                  radius={getRadius(location.density)}
                  fillColor={getColor(location.density)}
                  color="#fff"
                  weight={2}
                  opacity={0.8}
                  fillOpacity={0.7}
                >
                  <Popup closeButton={true}>
                    <Box sx={{ p: 1.5, minWidth: 240 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, mb: 1.5, color: '#1f2937', fontSize: '1rem' }}>
                        📍 {location.name}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b7280' }}>Total Complaints:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#667eea' }}>
                          {location.complaints}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b7280' }}>Resolved:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#10b981' }}>
                          {location.resolved}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b7280' }}>Resolution Rate:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                          {location.resolution_rate}%
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b7280' }}>Density vs Max:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: getColor(location.density) }}>
                          {((location.density / maxDensity) * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                    </Box>
                  </Popup>
                </CircleMarker>
                <Marker
                  position={[location.lat, location.lng]}
                  icon={labelIcon}
                  eventHandlers={{
                    click: () => {
                      // Marker click opens popup on circle
                    }
                  }}
                >
                  <Popup closeButton={false} autoClose={false}>
                    <Box sx={{ p: 2, minWidth: 260 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#1f2937', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                        📊 {location.name}
                      </Typography>

                      <Box sx={{ bgcolor: '#f3f4f6', p: 1.5, borderRadius: 1, mb: 1.5 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, display: 'block', mb: 0.5 }}>Total Complaints</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>
                                {location.complaints}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, display: 'block', mb: 0.5 }}>Resolved</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>
                                {location.resolved}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, display: 'block', mb: 0.5 }}>Resolution Rate</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                                {location.resolution_rate}%
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, display: 'block', mb: 0.5 }}>Density Level</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: getColor(location.density) }}>
                                {((location.density / maxDensity) * 100).toFixed(0)}%
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 1 }}>
                        <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                          Click on circle or number to view details
                        </Typography>
                      </Box>
                    </Box>
                  </Popup>
                </Marker>
              </React.Fragment>
            );
          })}
        </MapContainer>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f9fafb',
            borderRadius: '8px',
            border: '2px solid #e5e7eb'
          }}
        >
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#ffffff' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#6b7280', mb: 1 }}>
              🗺️ No Geographic Data Available
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Geographic hotspot data is loading...
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Legend */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          left: 20,
          bgcolor: '#ffffff',
          border: '2px solid #e5e7eb',
          borderRadius: 1,
          p: 2,
          zIndex: 400,
          minWidth: 240
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
          📊 Complaint Distribution Scale
        </Typography>
        <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem', display: 'block', mb: 1.5 }}>
          Relative to highest district ({maxDensity > 0 ? `${maxDensity}` : 'N/A'} cases)
        </Typography>
        {[
          { color: '#ef4444', label: '70-100%', range: 'Very High Density' },
          { color: '#f59e0b', label: '50-70%', range: 'High Density' },
          { color: '#eab308', label: '30-50%', range: 'Medium Density' },
          { color: '#10b981', label: '<30%', range: 'Low Density' }
        ].map((item, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: item.color,
                mr: 1.5,
                border: '2px solid #fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
            <Box>
              <Typography variant="caption" sx={{ color: '#1f2937', fontWeight: 600, display: 'block' }}>
                {item.label}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>
                {item.range}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ComplaintGeoMap;
